// Organizations (D56 screen 2): search by name, member email, app key or id; the newest first
// with no query. Searching is in the admin audit log; it views no single organization.
// Console D's list (APS-412 G1, admin-d.md sheet A): the head says how many and in what order,
// the search sits inline with its Buscar, and the table names each state in a word.
import { PageHead } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { orgsQuery } from '../api/queries'
import { DataTable, StatusWord } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'

export function OrganizationsPage() {
  const { t, date } = useI18n()
  const search = useSearch({ strict: false }) as { q?: string }
  const q = search.q ?? ''
  const navigate = useNavigate()
  const [text, setText] = useState(q)
  useEffect(() => setText(q), [q])
  const inputId = useId()
  const list = useQuery(orgsQuery(q))

  function submit(e: FormEvent) {
    e.preventDefault()
    navigate({ to: '/orgs', search: text.trim() ? { q: text.trim() } : {} })
  }

  return (
    <main id="main" className="pg g1-list">
      <PageHead
        title={t('orgs.title')}
        meta={
          list.data
            ? q
              ? t('orgs.metaFound', { count: list.data.length, q })
              : t('orgs.metaNewest', { count: list.data.length })
            : t('orgs.lede')
        }
      />
      <div className="g1-body">
        {/* The list in a card, its search in the card's head (brand v2, as the console's lists). */}
        <div className="sec g1-card">
          <search className="sec-h g1-card-h">
            <form className="g1-q" onSubmit={submit}>
              <label htmlFor={inputId} className="visually-hidden">
                {t('orgs.searchLabel')}
              </label>
              <input
                id={inputId}
                className="input"
                type="search"
                value={text}
                placeholder={t('orgs.placeholder')}
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => setText(e.target.value)}
                maxLength={200}
              />
              <button type="submit" className="btn sm">
                {t('orgs.searchSubmit')}
              </button>
            </form>
          </search>
          {list.isPending ? (
            <Loading />
          ) : list.isError ? (
            <ErrorState error={list.error} retry={() => list.refetch()} />
          ) : list.data.length === 0 ? (
            q ? (
              <Empty title={t('orgs.emptyFor', { q })}>
                <Link className="ul" to="/orgs" search={{}}>
                  {t('orgs.clear')}
                </Link>
              </Empty>
            ) : (
              <Empty title={t('orgs.empty')} />
            )
          ) : (
            <DataTable label={t('orgs.table')} minWidth={640}>
              <thead>
                <tr>
                  <th scope="col">{t('orgs.col.name')}</th>
                  <th scope="col">{t('orgs.col.plan')}</th>
                  <th scope="col">{t('orgs.col.status')}</th>
                  <th scope="col" className="num">
                    {t('orgs.col.trial')}
                  </th>
                  <th scope="col" className="num">
                    {t('orgs.col.members')}
                  </th>
                  <th scope="col">{t('orgs.col.created')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link
                        className="ttl"
                        to="/orgs/$orgId"
                        params={{ orgId: o.id }}
                        translate="no"
                      >
                        {o.name}
                      </Link>
                    </td>
                    <td className="cell-mono" translate="no">
                      {t(`plan.${o.plan}`)}
                    </td>
                    <td>
                      <StatusWord kind="org" status={o.status} />
                    </td>
                    <td className="num">
                      {o.trial_days_left === null
                        ? t('common.none')
                        : t('common.days', { count: o.trial_days_left })}
                    </td>
                    <td className="num">{o.members}</td>
                    <td>{date(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </div>
      </div>
    </main>
  )
}
